import React, { useEffect, useState } from "react";
import { Row, Col, Image, Button, Spinner, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Dropdown from "react-bootstrap/Dropdown";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TextInput from "components/TextInput";
import { Sentry } from "react-activity";
import {
  getMyFavorite,
  getMyProductSupport,
  deleteSupportList,
  updateSupportList,
  getProductList, // Add this API function to fetch all products
} from "api/ActivityService";
import { toast } from "react-toastify";
import Select from "react-select"; // For multi-select dropdown
import IMAGES from "constants/images";
import Loader from "components/Loader";

const sortOptions = [
  { label: "Most Recent", value: "most_recent" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const SortDropdown = ({ selectedOption, setSelectedOption }) => {
  return (
    <Dropdown align="start">
      <Dropdown.Toggle
        variant="light"
        className="d-flex align-items-center rounded-pill px-3"
      >
        <Icon icon="solar:sort-linear" fontSize={19} className="me-2" />
        <span className="iconFull">{selectedOption.label}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {sortOptions.map((option, index) => (
          <Dropdown.Item key={index} onClick={() => setSelectedOption(option)}>
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const MyFavouritesTab = ({ setShowModal1, refreshFavList }) => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const editSchema = yup.object().shape({
    name: yup.string().trim().min(1, "List Name is required"),
    products: yup.array().min(1, "At least one product is required"),
  });
  const [supports, setSupports] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // State for all available products
  const [favPage, setFavPage] = useState(1);
  const [is_disabled, setIsDisabled] = useState(false);
  const [is_disabled_remove, setIsDisabledRemove] = useState(false);
  const [supPage, setSupPage] = useState(1);
  const [favTotalPages, setFavTotalPages] = useState(1);
  const [supTotalPages, setSupTotalPages] = useState(1);
  const [loadingFav, setLoadingFav] = useState(false);
  const [loadingSup, setLoadingSup] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [visibleProducts, setVisibleProducts] = useState({});
  const [selectedOption, setSelectedOption] = useState(sortOptions[0] || []);
  const [editingList, setEditingList] = useState(null);
  const [listName, setListName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editSchema),
    defaultValues: {
      name: "",
      products: [],
    },
  });
  

  const selectedProducts = watch("products");

  // Fetch all products for the dropdown
  const fetchAllProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await getProductList(); // Implement this API call
      if (res.success) {
        const formattedProducts = res.body.map((product) => ({
          value: product._id,
          label: product.name,
          logoUrl: product.logoUrl,
        }));

        setAllProducts(formattedProducts);
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (showEditModal) {
      fetchAllProducts();
    }
  }, [showEditModal]);

  useEffect(() => {
    if (editingList) {
      reset({
        name: editingList.name,
        products: editingList.productId.map((product) => ({
          value: product._id,
          label: product.name,
          logoUrl: product.logoUrl,
        })),
      });
    }
  }, [editingList, reset]);

  const fetchFavorites = async (page = 1) => {
    setLoadingFav(true);
    try {
      const filter = selectedOption.value;
      const res = await getMyFavorite(filter, page, 3);
      if (res.success) {
        if (page === 1) {
          setFavorites(res.body.data);
        } else {
          setFavorites((prev) => [...prev, ...res.body.data]);
        }
        setFavTotalPages(res.body.total_pages);
      }
    } finally {
      setLoadingFav(false);
    }
  };

  const fetchSupportsfirstTime = async (page = 1) => {
    setLoadingSup(true);
    setPageLoading(true);
    try {
      const filter = selectedOption.value;
      const res = await getMyProductSupport(filter, page, 4); // limit = 3
      if (res.success) {
        // Filter out lists with no products
        const supportData = res.body.data.filter(
          (list) => list.productId.length > 0
        );

        if (page === 1) {
          setSupports(supportData);
        } else {
          setSupports((prev) => [...prev, ...supportData]);
        }

        const initialVisible = {};
        supportData.forEach((list) => {
          initialVisible[list._id] = 3;
        });
        setVisibleProducts((prev) => ({ ...prev, ...initialVisible }));

        setSupTotalPages(res.body.total_pages);
      }
    } finally {
      setLoadingSup(false);
      setPageLoading(false);
    }
  };
  const fetchSupports = async (page = 1) => {
    setLoadingSup(true);
    try {
      const filter = selectedOption.value;
      const res = await getMyProductSupport(filter, page, 4); // limit = 3
      if (res.success) {
        // Filter out lists with no products
        const supportData = res.body.data.filter(
          (list) => list.productId.length > 0
        );

        if (page === 1) {
          setSupports(supportData);
        } else {
          setSupports((prev) => [...prev, ...supportData]);
        }
        setSupPage(page);
        const initialVisible = {};
        supportData.forEach((list) => {
          initialVisible[list._id] = 3;
        });
        setVisibleProducts((prev) => ({ ...prev, ...initialVisible }));

        setSupTotalPages(res.body.total_pages);
      }
    } finally {
      setLoadingSup(false);
    }
  };
  const handleEditList = (list) => {
    setEditingList(list);
    setShowEditModal(true);
  };

  const handleUpdateList = async (data) => {
    setIsDisabled(true);
    const newName = data.name.trim();
    if (!newName) {
      toast.error("List name cannot be empty");
      setIsDisabled(false);
      return;
    }

    try {
      const payload = {
        name: newName.trim(),
        _id: editingList._id,
        productId: data.products.map((p) => p.value), // Send array of product IDs
      };

      const res = await updateSupportList(payload);
      if (res.success) {
        setSupports((prev) =>
          prev.map((list) =>
            list._id === editingList._id
              ? {
                  ...list,
                  name: newName,
                  productId: data.products.map((p) => ({
                    _id: p.value,
                    name: p.label,
                    logoUrl: p.logoUrl,
                    slug: p.slug || "", // Add slug if available
                  })),
                }
              : list
          )
        );
        toast.success("List updated successfully");
        setShowEditModal(false);
        setIsDisabled(false);
      } else {
        toast.error(res.message || "Failed to update list");
        setIsDisabled(false);
      }
    } catch (err) {
      console.error("Error updating list:", err);
      setIsDisabled(false);
      toast.error("Something went wrong while updating the list");
    } finally {
      setIsDisabled(false);
    }
  };

  const handleRemoveClick = (id) => {
    setListToDelete(id);
    setShowConfirmModal(true);
  };

  const handleConfirmRemove = async () => {
    if (listToDelete) {
      try {
        setIsDisabledRemove(true);
        const res = await deleteSupportList(listToDelete);
        if (res.success) {
          toast.success(res.message || "List removed successfully");
          setSupports((prev) =>
            prev.filter((list) => list._id !== listToDelete)
          );
        } else {
          toast.error(res.message || "Failed to remove list");
        }
      } catch (err) {
        console.error("Remove list error:", err);
        toast.error("Something went wrong while removing the list");
      } finally {
        setShowConfirmModal(false);
        setListToDelete(null);
        setIsDisabledRemove(false);
      }
    }
  };

  useEffect(() => {
    fetchFavorites(favPage);
    fetchSupports(supPage);
  }, [refreshFavList, selectedOption]);
  useEffect(() => {
    fetchSupportsfirstTime(1);
  }, []);
  // Custom option component for the select dropdown
  const Option = ({ innerProps, label, data }) => (
    <div {...innerProps} className="d-flex align-items-center p-2">
      <Image src={data.logoUrl} width={30} height={30} className="me-2" />
      <span>{label}</span>
    </div>
  );

  return pageLoading ? (
    <div className="loaderdiv">
      <Sentry color="#e91515" size={40} speed={1} animating={true} />
      {/* <Loader  style={{width: 100, height: 100}} /> */}
    </div>
  ) : (
    <>
      <div className="tab_1_style">
        {/* Edit List Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit List</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3">
            <Form onSubmit={handleSubmit(handleUpdateList)}>
              <TextInput
                labelText={"List Name"}
                placeholder={"Enter List Name"}
                {...register("name")}
                errorMessage={errors.name?.message}
              />

              <Form.Group className="mb-3">
                <Form.Label className="text-start d-block">
                  Select Products
                </Form.Label>
                <Select
                  isMulti
                  options={allProducts}
                  value={selectedProducts}
                  onChange={(selected) => setValue("products", selected)}
                  components={{ Option }}
                  isLoading={loadingProducts}
                  closeMenuOnSelect={false}
                  className={`react-select-container ${
                    errors.products ? "is-invalid" : ""
                  }`}
                  classNamePrefix="react-select"
                />
                {errors.products && (
                  <span className="text-danger mt-1 text-start d-block">
                    {errors.products.message}
                  </span>
                )}
              </Form.Group>

              <Button
                type="submit"
                variant="info"
                disabled={is_disabled}
                className="w-100 mt-4 border-radius-100"
              >
                {is_disabled ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
          centered
        >
          <Modal.Body className="p-4">
            <h3 className="text-danger mb-3">Are you sure?</h3>
            <p className="text-muted mb-7">
              you want to remove this from list? This action cannot be undone.
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                style={{ width: is_disabled_remove && "85px" }}
                onClick={handleConfirmRemove}
                disabled={is_disabled_remove}
              >
                {is_disabled_remove ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Yes Remove"
                )}
              </Button>
            </div>
          </Modal.Body>
        </Modal>

      
        <div className="software_header software_headerProfile mb-3 header_profile3">
          <h2>My Favorites</h2>
          <div className="lefAutoPro flex-wrap ">
            {/* <SortDropdown
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            /> */}
            <Button
              onClick={() => setShowModal1(true)}
              variant="info"
              className="border-radius-100 ms-2 px-4 hAnswers"
            >
              <Icon
                icon="material-symbols:add-rounded"
                fontSize={20}
                className="me-2"
              />
              Create New List
            </Button>
          </div>
        </div>
        <div className="software_header  mb-3 header_profile2 d-flex  justify-content-between">
          <div className="d-flex align-items-center">
      <Icon icon="mdi:arrow-left" style={{fontSize: "24px"}} className="me-4" onClick={() => navigate("/my-profile")}/>
      <h3 className="mb-0">My Favorites</h3>
      </div>
          <div className="lefAutoPro flex-wrap ">
            {/* <SortDropdown
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            /> */}
            <Button
              onClick={() => setShowModal1(true)}
              variant="info"
              className="border-radius-100 ms-2 px-4 hAnswers d-flex align-items-center"
            >
              <Icon
                icon="material-symbols:add-rounded"
                fontSize={20}
              />
              <span className="header_profile1 ms-2">Create New List</span>
            </Button>
          </div>
        </div>

        <Row className="equal-height-row gy-4">
          {(supports.length !== 0 || favorites.length !== 0) &&
            supports.map((list) => {
              const visibleCount = visibleProducts[list._id] || 3;
              console.log("list", list);
              return (
                <Col md={12} lg={6} key={list._id}>
                  <div className="favorite_list mb-4">
                    <div className="favorite_list_header">
                      <h3>{list.name}</h3>
                      {list?.canManage == "yes" && (
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            as="span"
                            style={{ cursor: "pointer" }}
                            className="d-inline-block dandiRemove"
                          >
                            <Icon icon="nrk:more" fontSize={25} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleEditList(list)}>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleRemoveClick(list._id)}
                            >
                              Remove
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </div>

                    <div className="favorite_list_body">
                      <div className="fav_list_Flex mb-3">
                        <Row className="g-3" style={{width:"100%"}}>
                          {list.productId
                            .slice(0, visibleCount)
                            .map((product) => (
                              <Col xs={4} key={product._id}>
                                <div className="favorite_list_item">
                                  <div
                                    className="iconBox"
                                    style={{ backgroundColor: "#FFDDE2" }}
                                  >
                                    <Link
                                      className="text-decoration-none text-dark"
                                      to={`/product-details/${product.slug}`}
                                    >
                                      <Image
                                        src={product.logoUrl}
                                        alt={product.name}
                                        style={{ width: 40, height: 40, objectFit: "contain" }}
                                      />
                                    </Link>
                                  </div>
                                  <strong
                                    className="text-center d-block truncate-2-lines "
                                    style={{ maxWidth: 100 }}
                                  >
                                    <Link
                                      className="text-decoration-none text-dark "
                                      to={`/product-details/${product.slug}`}
                                      style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100%",
                                        minHeight: "2.8em", // to avoid layout shift
                                      }}
                                    >
                                      {product.name}
                                    </Link>
                                  </strong>
                                </div>
                              </Col>
                            ))}
                        </Row>
                      </div>

                      {visibleCount < list.productId.length && (
                        <div className="d-flex justify-content-center">
                          <Button
                            variant="link"
                            className="view_all_link"
                            onClick={() =>
                              setVisibleProducts((prev) => ({
                                ...prev,
                                [list._id]: prev[list._id] + 3,
                              }))
                            }
                          >
                            View All
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              );
            })}
        </Row>

        {supPage < supTotalPages && (
          <Button
            variant="link"
            className="view_all_link d-block mx-auto"
            disabled={loadingSup}
            onClick={() => {
              const nextPage = supPage + 1;
              fetchSupports(nextPage);
            }}
          >
            {loadingSup ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Load More Lists"
            )}
          </Button>
        )}

        {favorites.length === 0 && supports.length === 0 && (
          <Col xs={12}>
            <div className="text-center py-5">
              <Image
                src={IMAGES.emptyState}
                alt=""
                  height="300px"
               width= "300px"
                className="mb-4"
                // className="imgUser"
              />
              <h4>No favorites or lists found</h4>
              <p className="text-muted">
                Start adding products to your favorites or create a new list
              </p>
            </div>
          </Col>
        )}
      </div>
    </>
  );
};

export default MyFavouritesTab;
